/**
 * CR098: Task 6 - Admin-only debug strip for deal cards
 * CR311: Added effective toggle values for ribbon and details_cta with sources
 * 
 * Shows configuration source info on cards to prove DB config is being applied.
 * Only renders in admin mode.
 */

import React from "react";
import type { DealTypeRegistryEntry, CardTemplateJson } from "@/providers/DealCardConfigProvider";

// CR311: Toggle sources for debugging
export interface EffectiveToggleSources {
  ribbon?: {
    effective: boolean;
    override?: boolean | undefined; // from overrides_json.surfaceOverrides.primary.blockToggles.ribbon
    template?: boolean | undefined; // from template_json.surfaceOverrides.primary.blockToggles.ribbon
  };
  details_cta?: {
    effective: boolean;
    override?: boolean | undefined;
    template?: boolean | undefined;
  };
}

interface DealCardConfigDebugStripProps {
  dealTypeKey: string;
  originalDealType: string | null;
  registry: DealTypeRegistryEntry | null;
  template: CardTemplateJson;
  isUnknownType: boolean;
  configLoaded: boolean;
  lastUpdatedAt: string | null;
  // CR311: Effective toggle values with sources
  effectiveToggles?: EffectiveToggleSources;
}

export function DealCardConfigDebugStrip({
  dealTypeKey,
  originalDealType,
  registry,
  template,
  isUnknownType,
  configLoaded,
  lastUpdatedAt,
  effectiveToggles,
}: DealCardConfigDebugStripProps) {
  // Count enabled sections
  const sectionsEnabled = Object.values(template.sections).filter(Boolean).length;
  const totalSections = Object.keys(template.sections).length;

  // CR311: Helper to render source info
  const renderSource = (override?: boolean, tpl?: boolean) => {
    if (override !== undefined) return `o:${override}`;
    if (tpl !== undefined) return `t:${tpl}`;
    return "default";
  };

  return (
    <div className="text-[10px] font-mono bg-slate-900 text-slate-300 px-2 py-1.5 rounded-t-lg flex flex-wrap gap-x-3 gap-y-0.5 items-center">
      {/* Deal Type */}
      <span>
        <span className="text-slate-500">type=</span>
        <span className={isUnknownType ? "text-red-400" : "text-emerald-400"}>
          {dealTypeKey}
        </span>
        {isUnknownType && (
          <span className="text-red-400 ml-1">⚠ UNKNOWN→UNCLASSIFIED</span>
        )}
        {originalDealType && originalDealType.toUpperCase() !== dealTypeKey && (
          <span className="text-amber-400 ml-1">(was: {originalDealType})</span>
        )}
      </span>

      {/* Ribbon Info */}
      <span>
        <span className="text-slate-500">ribbon=</span>
        <span className="text-blue-400">"{registry?.ribbon_text || "?"}"</span>
        <span className="text-slate-500">/</span>
        <span className="text-purple-400">{registry?.ribbon_theme || "?"}</span>
      </span>

      {/* CR311: Effective ribbon toggle */}
      {effectiveToggles?.ribbon && (
        <span>
          <span className="text-slate-500">rib.vis=</span>
          <span className={effectiveToggles.ribbon.effective ? "text-green-400" : "text-red-400"}>
            {effectiveToggles.ribbon.effective ? "✓" : "✗"}
          </span>
          <span className="text-slate-600 ml-0.5">
            ({renderSource(effectiveToggles.ribbon.override, effectiveToggles.ribbon.template)})
          </span>
        </span>
      )}

      {/* CR311: Effective details_cta toggle */}
      {effectiveToggles?.details_cta && (
        <span>
          <span className="text-slate-500">cta=</span>
          <span className={effectiveToggles.details_cta.effective ? "text-green-400" : "text-red-400"}>
            {effectiveToggles.details_cta.effective ? "✓" : "✗"}
          </span>
          <span className="text-slate-600 ml-0.5">
            ({renderSource(effectiveToggles.details_cta.override, effectiveToggles.details_cta.template)})
          </span>
        </span>
      )}

      {/* Sections */}
      <span>
        <span className="text-slate-500">sections=</span>
        <span className="text-cyan-400">{sectionsEnabled}/{totalSections}</span>
      </span>

      {/* Enabled Status */}
      <span>
        <span className="text-slate-500">enabled=</span>
        <span className={registry?.is_enabled ? "text-green-400" : "text-red-400"}>
          {registry?.is_enabled ? "yes" : "NO"}
        </span>
      </span>

      {/* Config Loaded */}
      <span>
        <span className="text-slate-500">loaded=</span>
        <span className={configLoaded ? "text-green-400" : "text-yellow-400"}>
          {configLoaded ? "✓" : "⏳"}
        </span>
      </span>

      {/* Updated At */}
      {lastUpdatedAt && (
        <span>
          <span className="text-slate-500">updated=</span>
          <span className="text-slate-400">
            {new Date(lastUpdatedAt).toLocaleDateString()}
          </span>
        </span>
      )}
    </div>
  );
}
